import { getIsStatus } from '@/Components/posts/status';
import { type Hub, PostStatusEnum } from '@/graphql/types';
import type { AppStore } from '@/inertia/AppStore';
import { useEffect, useRef } from 'react';
import type { CustomGreetingPayload } from './customElements/greeting';
import type { DFMessenger } from './messenger.types';

type Props = {
  pageType: AppStore['pageType'];
  znglist?: AppStore['znglist'];
  hub?: Hub;
};

export const __useDfMessenger__ = (props: Props) => {
  const df = useRef<DFMessenger>(document.querySelector('df-messenger'));

  const initPageType = useRef(props.pageType);
  const initId = useRef(props.znglist?.id || props.hub?.id);
  const hasRun = useRef(false);

  // specifically when the page type is changes from front_page to category or search
  useEffect(() => {
    if (props?.pageType !== initPageType.current) {
      initPageType.current = props.pageType;
      hasRun.current = false;
    } else if (!!props?.znglist?.id && props?.znglist?.id !== initId.current) {
      initId.current = props.znglist?.id;
      hasRun.current = false;
    } else if (!!props?.hub?.id && props?.hub?.id !== initId.current) {
      initId.current = props.hub?.id;
      hasRun.current = false;
    }
  }, [props?.pageType, props?.znglist?.id, props?.hub?.id]);

  useEffect(() => {
    if (!df.current || hasRun.current) {
      return;
    }
    hasRun.current = true;

    const args = {
      title: 'Zngly',
      message: 'How can I assist you today?',
      filter: [
        {
          dataStores: [
            `projects/zngly-corporate/locations/global/collections/default_collection/dataStores/${window.znRuntime?.dfDsId}`,
          ],
          filter: `post_status: ANY("publish")`,
        },
      ],
    };

    const decodeRelayId = (id?: string | null | undefined) => {
      if (!id) return null;

      if (Number.isInteger(Number(id))) {
        return Number(id).valueOf();
      }

      try {
        const decoded = atob(id);
        const parts = decoded.split(':');
        return parts?.length === 2 ? Number(parts[1]).valueOf() : null;
      } catch (error) {
        return null;
      }
    };

    if (props.pageType === 'znglist') {
      args.title = props.znglist?.title || args.title;

      const nodes = props.znglist?.znglist?.znglyContents ?? [];
      const nodesWithId = nodes.map((node) => decodeRelayId(node?.id)).filter((id) => !!id) as number[];

      const filter = nodesWithId.map((id) => `post_id = ${id}`).join(' OR ');

      if (
        getIsStatus(props?.znglist?.status, [PostStatusEnum.Private, PostStatusEnum.Hidden, PostStatusEnum.HiddenBS])
      ) {
        args.message =
          'How can I assist you with this Znglist. **Note**: you can only ask about the content on this page.';

        // "(post_id = 1 OR post_id = 2)"
        args.filter[0].filter = `(${filter})`;
      } else {
        args.filter[0].filter = `post_status: ANY("publish") OR (${filter})`;
      }
    } else if (props.pageType === 'hub') {
      args.title = props.hub?.title || args.title;

      // Get the ids from the hubPage node
      const ids = [] as number[];

      props?.hub?.connectionHubToHps?.edges?.forEach((edge) => {
        if (!edge?.node) return;

        const hubPageNode = edge?.node as NonNullable<NonNullable<Hub['connectionHubToHps']>['edges']>[number]['node'];
        hubPageNode.hubPage?.node?.connectionHpToCs?.edges?.forEach((edge) => {
          if (!edge?.node) return;
          const catalogNode = edge?.node as NonNullable<
            NonNullable<typeof hubPageNode.hubPage.node>['connectionHpToCs']
          >['edges'][number]['node'];

          const catalogId = decodeRelayId(catalogNode?.catalogId);
          if (catalogId) {
            ids.push(catalogId);
          }
        });
      });

      const uniqueIds = Array.from(new Set(ids)); // Remove duplicates

      const filter = uniqueIds.map((id) => `post_id = ${id}`).join(' OR ');
      if (getIsStatus(props?.hub?.status, [PostStatusEnum.Private, PostStatusEnum.Hidden, PostStatusEnum.HiddenBS])) {
        args.message = 'How can I assist you with this Hub. **Note**: you can only ask about the content on this page.';

        args.filter[0].filter = `(${filter})`;
      } else {
        args.filter[0].filter = `post_status: ANY("publish") OR (${filter})`;
      }
    } else if (props.pageType === 'front_page') {
      args.title = 'the Chatbot Preview';
      args.message = 'How can I assist you today?';

      // Default filter for front page
      args.filter[0].filter = `post_status: ANY("publish")`;

      // Don't return here - let the shared code below handle rendering
    } else {
      df.current.setQueryParameters({
        searchConfig: {
          filterSpecs: args.filter,
          boostSpecs: [],
        },
      });
      return;
    }
    // TODO: This code is commented out because starting a new session is currently not required. Uncomment if session management changes in the future.
    // df.current.startNewSession({ retainHistory: true });

    // Don't start a new session - just update parameters and show custom greeting
    df.current.setQueryParameters({
      searchConfig: {
        filterSpecs: args.filter,
        boostSpecs: [],
      },
    });

    df.current.renderCustomCard([
      {
        type: 'custom_template',
        name: 'df-custom-greeting',
        payload: JSON.stringify({
          greeting: {
            title: `Welcome to ${args.title}!`,
            message: args.message,
            // icon: 'https://example.com/icon.png', // Replace with your icon URL
          },
        } as CustomGreetingPayload),
      },
    ]);
  }, [props.pageType, props.hub?.id, props.znglist?.id]);
};
